import { isNil, isNonEmptyRecord, isNonEmptyString } from '~/utilities/type-guards';
import type { FieldPolicy } from '@apollo/client';
import { relayStylePagination } from '@apollo/client/utilities';

export const APOLLO_CACHE_KEY_RELAY_STYLE_PAGINATION_SCOPED = 'scoped';

/**
 * ASSUMPTION:
 * If search, filters, or orderBy are present, we treat the query
 * as coming from either the Folder or Subfolder pages.
 *
 * The other assumption is that when these values change
 * a manual "refetch" is kicked off to reset the data with the new args back to a single keyArgs cache entry.
 * Otherwise, Apollo may have inconsistent or surprising merge behavior when switching between filtered views (e.g. filter results merging instead of being replaced)
 * (e.g. medias:customView)
 *
 * Future Thinking:
 *   * This may be not be the best long-term solution
 *   * When the Folder page has its large monolith GQL query broken up into smaller queries (e.g. media list, stats, etc), this might be able to be removed
 *
 * Rationale:
 * - Supporting every possible search + filter + sort combination would:
 *   * Greatly increase cache complexity
 *   * Cause severe cache fragmentation
 *   * Make CRUD operations with optimistic updates (create, delete, move, copy) unmanageable
 *
 * Simplification:
 * - We bucket all combinations into a single cache entry.
 * - This allows other queries and mutations (e.g., Move & Copy) that fetch or update
 *   list data and total counts to continue leveraging the same cache entry reliably.
 *
 * Tradeoff:
 * - We lose fine-grained cache separation for every combination,
 *   but gain a maintainable and predictable caching strategy for now.
 *
 * Risks:
 * - In the future, if other pages want to custom SORT these media connections, this may cause cache conflicts again.
 */
const hasScopedMediaKeyArgs = (args?: Record<string, unknown> | null) => {
  if (isNil(args)) return false;

  // ASSUMPTIONS: Query is a non-empty string, orderBy is a non-empty string, filters is a non-empty record (e.g. Filter Selection Model JSON Object)
  return (
    isNonEmptyString(args.query) || isNonEmptyString(args.orderBy) || isNonEmptyRecord(args.filters)
  );
};

export const relayStylePaginationScopedMedias = (): FieldPolicy => {
  const base = relayStylePagination();

  return {
    read: base.read,
    merge: base.merge,
    keyArgs(args) {
      // "medias" -> Default view (no query/filters/orderBy)
      // "medias:scoped" -> Query, filters, or orderBy applied
      return hasScopedMediaKeyArgs(args) ? APOLLO_CACHE_KEY_RELAY_STYLE_PAGINATION_SCOPED : false;
    },
  };
};
